import { useState, useEffect } from "react";
import { X, Home, BookOpen, Users, FileText, Settings, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function Sidebar({ isOpen, toggleSidebar }: { isOpen: boolean; toggleSidebar: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      name: "Create Story",
      href: "/",
      icon: Sparkles,
    },
    {
      name: "My Stories",
      href: "/stories",
      icon: BookOpen,
    },
    {
      name: "Characters",
      href: "/characters",
      icon: Users,
    },
    {
      name: "Templates",
      href: "/templates",
      icon: FileText,
    },
    {
      name: "Settings",
      href: "/settings",
      icon: Settings,
    },
  ];

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (window.innerWidth < 768) {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      {/* Mobile overlay */}
      <div 
        className={cn(
          "fixed inset-0 z-30 bg-black/50 transition-opacity md:hidden",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={toggleSidebar}
        aria-hidden="true"
      />
      
      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed inset-y-0 left-0 z-40 bg-white transform transition-transform duration-300 ease-in-out",
          "flex flex-col w-64",
          isOpen ? "translate-x-0" : "-translate-x-full",
          "md:translate-x-0 md:transition-none md:relative md:sticky md:top-16 md:h-[calc(100vh-4rem)]",
          !isOpen && "md:w-0 md:hidden"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b md:hidden">
          <h1 
            onClick={() => navigate("/")}
            className="text-xl font-bold cursor-pointer select-none"
          >
            <span className="text-[#8B5CF6]">Story</span>
            <span className="text-[#7C3AED]">Book</span>
          </h1>
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 md:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <button
                key={item.name}
                onClick={() => {
                  navigate(item.href);
                  if (window.innerWidth < 768) {
                    toggleSidebar();
                  }
                }}
                className={cn(
                  "flex items-center w-full px-3 py-2.5 text-sm rounded-md transition-colors",
                  "hover:bg-accent/10 active:bg-accent/20",
                  isActive
                    ? "bg-accent text-white"
                    : "text-gray-700 hover:text-accent"
                )}
              >
                <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                <span className="truncate">{item.name}</span>
              </button>
            );
          })}
        </nav>
      </aside>
      
      {/* Desktop toggle button */}
      <Button
        variant="outline"
        size="icon"
        onClick={toggleSidebar}
        className="fixed left-0 top-1/2 z-50 -translate-y-1/2 rounded-r-full rounded-l-none border-l-0 shadow-md hidden md:flex"
      >
        {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </Button>
    </>
  );
}
